import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createExportJob, pollExporterJobStatus } from 'modules/api/inventory';
import { isCanceledError } from 'modules/api/request';
import { addToastNotification } from 'store/notification/reducer';
import { cancelFunctions } from 'utils/cancelFunctions';
import { getMarketId } from 'store/publisher/selectors';
import { useExportJobHistory } from './useExportJobHistory';
import { ExportJob, ExportJobStatus } from '../types/ExportJob.types';

const POLL_INTERVAL_MS = 5000;

const isJobActive = (status: ExportJobStatus): boolean => status === 'PROCESSING';

interface Result {
  jobHistory: ExportJob[];
  isExportDisabled: boolean;
  generateExport: () => Promise<void>;
}

export const useExportJob = (): Result => {
  const dispatch = useDispatch();
  const { jobHistory, setJobHistory, refetchJobHistory } = useExportJobHistory();
  const market = useSelector(getMarketId);
  const [isGenerating, setIsGenerating] = useState(false);
  const [pollAttempt, setPollAttempt] = useState(0);

  const activeJobId = useMemo(() => jobHistory.find((job) => isJobActive(job.status))?.jobId ?? null, [jobHistory]);
  const isExportDisabled = useMemo(() => isGenerating || activeJobId !== null, [isGenerating, activeJobId]);

  useEffect(() => {
    if (!activeJobId) return undefined;

    let retryTimeout: ReturnType<typeof setTimeout>;

    const poll = pollExporterJobStatus({
      market,
      jobId: activeJobId,
      cancelFunctions,
      fallback: () => {
        retryTimeout = setTimeout(async () => {
          await refetchJobHistory();
          setPollAttempt((attempt) => attempt + 1);
        }, POLL_INTERVAL_MS);
      },
    });

    poll(({ data }: { data: ExportJob }) => {
      setJobHistory((current) => current.map((job) => (job.jobId === activeJobId ? { ...job, ...data } : job)));

      return isJobActive(data.status);
    }, POLL_INTERVAL_MS);

    return () => {
      clearTimeout(retryTimeout);
      cancelFunctions.GET_EXPORTER_JOB_STATUS?.();
    };
  }, [market, activeJobId, setJobHistory, pollAttempt]);

  const generateExport = async (): Promise<void> => {
    setIsGenerating(true);

    try {
      await createExportJob(market, cancelFunctions);
      await refetchJobHistory();
    } catch (error) {
      if (isCanceledError(error)) return;

      dispatch(
        addToastNotification({
          header: 'Generate export',
          description: 'Failed to generate the export. Please try again later.',
          type: 'Failure',
          timeout: 10000,
        }),
      );
    } finally {
      setIsGenerating(false);
    }
  };

  return { jobHistory, isExportDisabled, generateExport };
};
